import React, { useState, useEffect } from 'react';
import { Table, Checkbox, Card, message, Input, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';

const CDifundirCereales = () => {
    const [mensajes, setMensajes] = useState([]);
    const [filteredMensajes, setFilteredMensajes] = useState([]);
    const [selectedNumeros, setSelectedNumeros] = useState([]);
    const [filteredSelectedNumeros, setFilteredSelectedNumeros] = useState([]);
    // Add cooldown state variables
    const [diffusionCooldown, setDiffusionCooldown] = useState(false);
    const [cooldownTimer, setCooldownTimer] = useState(0);

    // Filtros para la tabla superior
    const [searchNumero, setSearchNumero] = useState('');
    const [searchCuenta, setSearchCuenta] = useState('');
    const [searchNombre, setSearchNombre] = useState('');

    // Filtros para la tabla inferior (seleccionados)
    const [selectedSearchNumero, setSelectedSearchNumero] = useState('');
    const [selectedSearchCuenta, setSelectedSearchCuenta] = useState('');
    const [selectedSearchNombre, setSelectedSearchNombre] = useState('');

    const user = JSON.parse(localStorage.getItem('user'));
    const empresaId = user?.user?.idCooperativa;

    useEffect(() => {
        const fetchMensajes = async () => {
            try {
                const mensajesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/mensajes/${empresaId}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });

                const datosTransformados = mensajesResponse.data.flatMap(mensaje =>
                    mensaje.datosCuenta.map(cuenta => ({
                        key: `${mensaje.numero}-${cuenta.cuenta}`,
                        numero: String(mensaje.numero || '').trim(),
                        cuenta: String(cuenta.cuenta || '').trim(),
                        nombre: cuenta.nombre || '',
                    }))
                );

                setMensajes(datosTransformados);
                setFilteredMensajes(datosTransformados);

                const difundirResponse = await axios.get(`${process.env.REACT_APP_API_URL}/difundir-cereales/${empresaId}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });

                // Ajustamos para manejar la estructura real de la respuesta
                if (difundirResponse.data?.telefonos) {
                    const telefonos = difundirResponse.data.telefonos.map(tel => ({
                        key: `${tel.numero}-${tel.cuenta}`,
                        numero: String(tel.numero || '').trim(),
                        cuenta: String(tel.cuenta || '').trim(),
                        nombre: tel.nombre || '',
                    }));
                    setSelectedNumeros(telefonos);
                    setFilteredSelectedNumeros(telefonos);
                } else {
                    console.log('No se encontraron teléfonos en la respuesta:', difundirResponse.data);
                }
            } catch (error) {
                console.error('Error al cargar datos:', error);
                message.error('Error al cargar los datos.');
            }
        };

        fetchMensajes();

        // Check if there's a stored cooldown timestamp in localStorage
        const storedCooldownTime = localStorage.getItem('diffusionCooldownUntil');
        if (storedCooldownTime) {
            const cooldownUntil = parseInt(storedCooldownTime, 10);
            const now = Date.now();
            
            if (cooldownUntil > now) {
                // Cooldown is still active
                setDiffusionCooldown(true);
                const remainingTime = Math.ceil((cooldownUntil - now) / 1000);
                setCooldownTimer(remainingTime);
                
                // Start the timer
                startCooldownTimer(remainingTime);
            } else {
                // Cooldown has expired, clear from localStorage
                localStorage.removeItem('diffusionCooldownUntil');
            }
        }
    }, [empresaId, user.token]);

    // Function to start the countdown timer
    const startCooldownTimer = (seconds) => {
        let remainingSeconds = seconds;
        
        const timerInterval = setInterval(() => {
            remainingSeconds -= 1;
            setCooldownTimer(remainingSeconds);
            
            if (remainingSeconds <= 0) {
                clearInterval(timerInterval);
                setDiffusionCooldown(false);
                localStorage.removeItem('diffusionCooldownUntil');
                message.success('¡Ya puede difundir nuevamente los precios de cereales!');
            }
        }, 1000);
        
        // Store the interval ID to clear it if component unmounts
        return () => clearInterval(timerInterval);
    };

    const isRecordSelected = (record) => {
        return selectedNumeros.some(
            (item) =>
                String(item.numero).trim() === String(record.numero).trim() &&
                String(item.cuenta).trim() === String(record.cuenta).trim()
        );
    };

    const handleCheckboxChange = (record) => {
        const isSelected = isRecordSelected(record);

        if (isSelected) {
            const newSelectedNumeros = selectedNumeros.filter(
                (item) => !(String(item.numero).trim() === String(record.numero).trim() && String(item.cuenta).trim() === String(record.cuenta).trim())
            );
            setSelectedNumeros(newSelectedNumeros);
            setFilteredSelectedNumeros(applySelectedFilter(newSelectedNumeros));
        } else {
            const newSelectedNumeros = [...selectedNumeros, { ...record }];
            setSelectedNumeros(newSelectedNumeros);
            setFilteredSelectedNumeros(applySelectedFilter(newSelectedNumeros));
        }
    };

    const handleGuardarLista = async () => {
        if (selectedNumeros.length === 0) {
            message.error('Debe seleccionar al menos un número.');
            return;
        }

        try {
            const telefonosData = selectedNumeros.map(({ numero, cuenta, nombre }) => ({
                numero,
                cuenta,
                nombre,
            }));

            await axios.post(
                `${process.env.REACT_APP_API_URL}/difundir-cereales`,
                { idCooperativa: empresaId, telefonos: telefonosData },
                { headers: { Authorization: `Bearer ${user.token}`, 'Content-Type': 'application/json' } }
            );

            message.success('Lista de números guardada correctamente.');
        } catch (error) {
            console.error('Error al guardar la lista:', error);
            message.error('Error al guardar la lista de números.');
        }
    };

    const handleDifundirPrecios = async () => {
        // Check if cooldown is active
        if (diffusionCooldown) {
            const minutes = Math.floor(cooldownTimer / 60);
            const seconds = cooldownTimer % 60;
            message.warning(`Debe esperar ${minutes} minutos y ${seconds} segundos para volver a difundir los precios.`);
            return;
        }
    
        // Deshabilitamos el botón inmediatamente
        setDiffusionCooldown(true);
        setCooldownTimer(300);
            
        // Guardamos el tiempo de finalización del cooldown en localStorage
        const cooldownUntil = Date.now() + (300 * 1000);
        localStorage.setItem('diffusionCooldownUntil', cooldownUntil.toString());
            
        // Iniciamos el temporizador
        startCooldownTimer(300);
    
        try {
            if (empresaId !== '6742343bd7e810d622f99424') {
                message.error('No tiene habilitado el envío de precios de cereales.');
                return;
            }
    
            const linkBot = 'https://bot-carabelas.onrender.com';
            console.log('Enviando solicitud a:', `${linkBot}/difundir-cereales`);
            const response = await axios.post(`${linkBot}/difundir-cereales`);
            
            console.log('Respuesta del backend:', response.data); // Depuración
    
            if (response.data && response.data.success) {
                message.success('Precios de cereales difundidos correctamente.');
            } else {
                message.error(response.data?.message || 'Error al difundir los precios.');
                // No desactivamos el cooldown en caso de error para evitar múltiples intentos
            }
        } catch (error) {
            console.error('Error al difundir precios:', {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message,
            });
            message.error('Error al difundir los precios de cereales. Revisa la consola para más detalles.');
            // No desactivamos el cooldown en caso de error para evitar múltiples intentos
        }
    };

    const handleProbarNuevaPlantilla = async () => {
        try {
            if (empresaId !== '6742343bd7e810d622f99424') {
                message.error('No tiene habilitada la prueba de plantilla utility.');
                return;
            }

            const linkBot = 'https://bot-carabelas.onrender.com';
            const response = await axios.post(`${linkBot}/difundir-cereales-utility-test`);

            if (response.data && response.data.success) {
                message.success('Prueba de nueva plantilla enviada al número 5493534226477.');
            } else {
                message.error(response.data?.message || 'Error al probar la nueva plantilla.');
            }
        } catch (error) {
            console.error('Error al probar nueva plantilla:', {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message,
            });
            message.error(error.response?.data?.message || 'Error al probar la nueva plantilla.');
        }
    };

    const applyFilter = (data) => {
        return data.filter((item) => {
            const numero = String(item.numero || '').toLowerCase();
            const cuenta = String(item.cuenta || '').toLowerCase();
            const nombre = String(item.nombre || '').toLowerCase();

            return (
                (!searchNumero || numero.includes(searchNumero.toLowerCase())) &&
                (!searchCuenta || cuenta.includes(searchCuenta.toLowerCase())) &&
                (!searchNombre || nombre.includes(searchNombre.toLowerCase()))
            );
        });
    };

    const applySelectedFilter = (data) => {
        return data.filter((item) => {
            const numero = String(item.numero || '').toLowerCase();
            const cuenta = String(item.cuenta || '').toLowerCase();
            const nombre = String(item.nombre || '').toLowerCase();

            return (
                (!selectedSearchNumero || numero.includes(selectedSearchNumero.toLowerCase())) &&
                (!selectedSearchCuenta || cuenta.includes(selectedSearchCuenta.toLowerCase())) &&
                (!selectedSearchNombre || nombre.includes(selectedSearchNombre.toLowerCase()))
            );
        });
    };

    const handleSearchNumero = (e) => {
        setSearchNumero(e.target.value);
        setFilteredMensajes(applyFilter(mensajes));
    };

    const handleSearchCuenta = (e) => {
        setSearchCuenta(e.target.value);
        setFilteredMensajes(applyFilter(mensajes));
    };

    const handleSearchNombre = (e) => {
        setSearchNombre(e.target.value);
        setFilteredMensajes(applyFilter(mensajes));
    };

    const handleSelectedSearchNumero = (e) => {
        setSelectedSearchNumero(e.target.value);
        setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
    };

    const handleSelectedSearchCuenta = (e) => {
        setSelectedSearchCuenta(e.target.value);
        setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
    };

    const handleSelectedSearchNombre = (e) => {
        setSelectedSearchNombre(e.target.value);
        setFilteredSelectedNumeros(applySelectedFilter(selectedNumeros));
    };

    const handleRemoveNumero = (numero, cuenta) => {
        const newSelectedNumeros = selectedNumeros.filter(
            (item) => !(String(item.numero).trim() === String(numero).trim() && String(item.cuenta).trim() === String(cuenta).trim())
        );
        setSelectedNumeros(newSelectedNumeros);
        setFilteredSelectedNumeros(applySelectedFilter(newSelectedNumeros));
    };

    const handleSelectAll = (selected) => {
        if (selected) {
            const newSelectedItems = [...selectedNumeros];
            filteredMensajes.forEach((record) => {
                if (!isRecordSelected(record)) {
                    newSelectedItems.push(record);
                }
            });
            setSelectedNumeros(newSelectedItems);
            setFilteredSelectedNumeros(applySelectedFilter(newSelectedItems));
        } else {
            const newSelectedItems = selectedNumeros.filter(
                (item) => !filteredMensajes.some((record) => record.numero === item.numero && record.cuenta === item.cuenta)
            );
            setSelectedNumeros(newSelectedItems);
            setFilteredSelectedNumeros(applySelectedFilter(newSelectedItems));
        }
    };

    const areAllSelected =
        filteredMensajes.length > 0 && filteredMensajes.every((record) => isRecordSelected(record));

    const columns = [
        {
            title: '',
            dataIndex: 'checkbox',
            key: 'checkbox',
            width: 50,
            render: (_, record) => (
                <Checkbox checked={isRecordSelected(record)} onChange={() => handleCheckboxChange(record)} />
            ),
        },
        { title: 'Número de Teléfono', dataIndex: 'numero', key: 'numero' },
        { title: 'Número de Cuenta', dataIndex: 'cuenta', key: 'cuenta' },
        { title: 'Nombre de Cuenta', dataIndex: 'nombre', key: 'nombre' },
    ];

    const selectedColumns = [
        { title: 'Número de Teléfono', dataIndex: 'numero', key: 'numero' },
        { title: 'Número de Cuenta', dataIndex: 'cuenta', key: 'cuenta' },
        { title: 'Nombre de Cuenta', dataIndex: 'nombre', key: 'nombre' },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <Button type="link" danger onClick={() => handleRemoveNumero(record.numero, record.cuenta)}>
                    Eliminar
                </Button>
            ),
        },
    ];

    // Format the cooldown timer for display
    const formatCooldownTime = () => {
        const minutes = Math.floor(cooldownTimer / 60);
        const seconds = cooldownTimer % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    return (
        <div className="p-4">
            <Card title="Difundir Cereales" className="mb-4">
                <div style={{ display: 'flex', width: '100%', gap: '16px', marginTop: '16px', marginBottom: '16px', alignItems: 'center' }}>
                    <Input
                        value={searchNumero}
                        onChange={handleSearchNumero}
                        placeholder="Buscar por número de telefono"
                        prefix={<SearchOutlined />}
                        style={{ flex: '1 1 0', minWidth: '0' }}
                    />
                    <Input
                        value={searchCuenta}
                        onChange={handleSearchCuenta}
                        placeholder="Buscar por cuenta"
                        prefix={<SearchOutlined />}
                        style={{ flex: '1 1 0', minWidth: '0' }}
                    />
                    <Input
                        value={searchNombre}
                        onChange={handleSearchNombre}
                        placeholder="Buscar por nombre"
                        prefix={<SearchOutlined />}
                        style={{ flex: '1 1 0', minWidth: '0' }}
                    />
                    <Button
                        onClick={() => {
                            setSearchNumero('');
                            setSearchCuenta('');
                            setSearchNombre('');
                            setFilteredMensajes(mensajes);
                        }}
                        style={{ marginLeft: 'auto' }}
                    >
                        Limpiar filtros
                    </Button>
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <Checkbox checked={areAllSelected} onChange={(e) => handleSelectAll(e.target.checked)}>
                        {areAllSelected ? 'Deseleccionar todos' : 'Seleccionar todos los filtrados'}
                    </Checkbox>
                </div>

                <Table
                    dataSource={filteredMensajes}
                    columns={columns}
                    rowKey={(record) => `${record.numero}-${record.cuenta}`}
                    pagination={{ pageSize: 5 }}
                />

                <Card title="Números Seleccionados" style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', width: '100%', gap: '16px', marginTop: '16px', marginBottom: '16px', alignItems: 'center' }}>
                        <Input
                            value={selectedSearchNumero}
                            onChange={handleSelectedSearchNumero}
                            placeholder="Buscar por número de telefono"
                            prefix={<SearchOutlined />}
                            style={{ flex: '1 1 0', minWidth: 0 }}
                        />
                        <Input
                            value={selectedSearchCuenta}
                            onChange={handleSelectedSearchCuenta}
                            placeholder="Buscar por cuenta"
                            prefix={<SearchOutlined />}
                            style={{ flex: '1 1 0', minWidth: 0 }}
                        />
                        <Input
                            value={selectedSearchNombre}
                            onChange={handleSelectedSearchNombre}
                            placeholder="Buscar por nombre"
                            prefix={<SearchOutlined />}
                            style={{ flex: '1 1 0', minWidth: 0 }}
                        />
                        <Button
                            onClick={() => {
                                setSelectedSearchNumero('');
                                setSelectedSearchCuenta('');
                                setSelectedSearchNombre('');
                                setFilteredSelectedNumeros(selectedNumeros);
                            }}
                            style={{ marginLeft: 'auto' }}
                        >
                            Limpiar filtros
                        </Button>
                    </div>
                    <Table
                        dataSource={filteredSelectedNumeros}
                        columns={selectedColumns}
                        rowKey={(record) => `${record.numero}-${record.cuenta}`}
                        pagination={{ pageSize: 5 }}
                    />
                </Card>

                <div style={{ display: 'flex', gap: '16px', marginTop: '16px', alignItems: 'center' }}>
                    <Button type="primary" onClick={handleGuardarLista}>
                        Guardar Lista
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleDifundirPrecios}
                        disabled={diffusionCooldown}
                    >
                        Difundir Precio Cereales
                    </Button>
                    {/*
                    <Button onClick={handleProbarNuevaPlantilla}>
                        Probar nueva plantilla (solo 5493534226477)
                    </Button>
                    */}
                    
                    {diffusionCooldown && (
                        <div style={{ marginLeft: '10px', color: '#ff4d4f' }}>
                            Tiempo restante: {formatCooldownTime()}
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default CDifundirCereales;